// App-level policies, pure and unit-tested — port of TrailBivvy/App/AppPreferences.swift (TrailChoicePolicy,
// LaunchDestinationPolicy, TrailSwitchPolicy, DemoTrailPolicy) for the cases the PWA has (no synthetic demo in
// production, iOS #42).

import type { EpochMs, TrailCatalogEntry } from '../core/models';

export const TrailChoicePolicy = {
  /** The stored trail when it is still in the catalogue; production follows no trail until one is chosen (iOS #14). */
  initialTrailID(stored: string | undefined, isAvailable: (id: string) => boolean): string | undefined {
    return stored && isAvailable(stored) ? stored : undefined;
  },
};

export const DemoTrailPolicy = {
  isDemo(entry: TrailCatalogEntry): boolean {
    return entry.dataQuality === 'demoSynthetic' || entry.isSynthetic;
  },
  /** The synthetic Via Alpina demo is never listed in production (iOS #42). */
  listedEntries(entries: readonly TrailCatalogEntry[]): TrailCatalogEntry[] {
    return entries.filter((e) => !DemoTrailPolicy.isDemo(e) && e.packResource);
  },
};

export type LaunchDestination = 'trailList' | 'map';

export const LaunchDestinationPolicy = {
  /** Navigation left running counts as interrupted for 12 hours (iOS LA-9). */
  interruptedNavigationLimit: 12 * 3_600_000,

  navigationWasInterrupted(runningAt: EpochMs | undefined, now: EpochMs): boolean {
    return runningAt !== undefined && Math.abs(now - runningAt) <= LaunchDestinationPolicy.interruptedNavigationLimit;
  },

  /** Every launch opens on the trail list, except for a hiker out on the trail (iOS LA-1…LA-12). */
  destination(situation: { isNavigating: boolean; hasRecording: boolean; navigationInterrupted: boolean }): LaunchDestination {
    if (situation.isNavigating || situation.navigationInterrupted || situation.hasRecording) return 'map';
    return 'trailList';
  },
};

export type SwitchConsequence =
  | { kind: 'replacesRoute' }
  | { kind: 'removesTrail' }
  | { kind: 'dropsCustomRoute' }
  | { kind: 'stopsNavigation'; canRestart: boolean }
  | { kind: 'recordingContinues' }
  | { kind: 'keepsDeviceData' };

export interface SwitchSituation {
  hasRoute: boolean;
  isNavigating: boolean;
  isRecording: boolean;
  hasCustomRoute: boolean;
}

function details(situation: SwitchSituation, followsAnotherTrail: boolean): SwitchConsequence[] {
  const out: SwitchConsequence[] = [];
  if (situation.hasCustomRoute) out.push({ kind: 'dropsCustomRoute' });
  if (situation.isNavigating) out.push({ kind: 'stopsNavigation', canRestart: followsAnotherTrail });
  if (situation.isRecording) out.push({ kind: 'recordingContinues' });
  out.push({ kind: 'keepsDeviceData' });
  return out;
}

export const TrailSwitchPolicy = {
  hasCustomRoute(storedStageIDs: readonly string[]): boolean {
    return storedStageIDs.length > 0;
  },
  /** Consequences to confirm before following another trail; undefined when no confirmation is needed. */
  switchConfirmation(situation: SwitchSituation): SwitchConsequence[] | undefined {
    if (!situation.isNavigating && !situation.isRecording && !situation.hasCustomRoute) return undefined;
    return [...(situation.hasRoute ? [{ kind: 'replacesRoute' } as const] : []), ...details(situation, true)];
  },
  stopFollowingConfirmation(situation: SwitchSituation): SwitchConsequence[] {
    return [{ kind: 'removesTrail' }, ...details(situation, false)];
  },
};
